import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  List,
  useDataProvider,
  useGetList,
  TopToolbar,
  CreateButton,
  ExportButton,
  DateInput,
  TextInput,
  AutocompleteArrayInput,
  SelectInput,
  Button as RaButton,
  useTranslate,
  useNotify,
  useRedirect,
  SaveButton,
  DeleteButton,
  ToolbarProps,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  FunctionField,
  EditButton,
  Pagination,
  useListContext,
} from "react-admin";
import { httpClient, API_URL } from "../../httpClient";
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import { Link, useSearchParams } from 'react-router-dom';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Toolbar, Tabs, Tab, Chip, Tooltip, Alert } from "@mui/material";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { JobApplicationColumn } from "./JobApplicationColumn";
import { JobApplication } from "./JobApplicationCard";
import { CloseApplicationDialog } from "./CloseApplicationDialog";
import { CreateDialog, EditDialog, StackedFilters, FiltersConfig } from "@react-admin/ra-form-layout";
import { JobApplicationForm } from ".";
import { ReferenceArrayInput, AuditTimelineCompact, PhoneField } from "../../components";
import { useRecordContext } from "react-admin";

// Empty component for operators that don't need input (null/not_null)
const EmptyInput = () => <Box sx={{ display: 'none' }} />;

// Force pt-BR formatting for dates in table columns (independent of browser locale)
const DATE_FIELD_LOCALES = 'pt-BR';

type StatusData = {
  id: number;
  name: string;
  order: number;
  hidesFromKanban?: boolean;
  autoHideAfterDays?: number | null;
  isPostHire?: boolean;
  isTerminal?: boolean;
  requiresClosureReason?: boolean;
  triggersOutcome?: string | null;
};

function getStatusStages(statuses: Array<StatusData>) {  
  // Filter out statuses that should be hidden from kanban
  // (hidesFromKanban = true statuses are handled by application filtering, not stage filtering)
  return statuses.map((status) => ({
    id: String(status.id),
    value: String(status.id),
    label: status.name,
    hidesFromKanban: status.hidesFromKanban,
    autoHideAfterDays: status.autoHideAfterDays,
    isPostHire: status.isPostHire,
    triggersOutcome: status.triggersOutcome,
  }));
}

function getApplicationsByStatus(applications: JobApplication[], statusStages: Array<{ id: string; value: string; label: string }>): Record<string, JobApplication[]> {
  const byStatus: Record<string, JobApplication[]> = {};
  
  // Initialize with all status stages
  statusStages.forEach((stage) => {
    byStatus[stage.id] = [];
  });
  
  // All applications have a statusId - no need to handle null cases
  if (applications) {
    applications.forEach((app) => {
      // Find the status stage that matches this application's statusId
      const matchingStage = statusStages.find(stage => stage.value === String(app.statusId));
      if (matchingStage) {
        byStatus[matchingStage.id].push(app);
      }
    });
  }
  
  return byStatus;
}

const CustomListActions = ({ onCreateClick }: { onCreateClick?: () => void }) => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const currentView = (searchParams.get('view') as ViewType) || 'pipeline';
  
  // Custom CreateButton that maintains view parameter
  const createUrl = currentView === 'list' 
    ? '/job-application/create?view=list'
    : '/job-application/create';
  
  return (
    <TopToolbar>
      {onCreateClick ? (
        <RaButton
          label={translate('ra.action.create')}
          onClick={onCreateClick}
          startIcon={<AddIcon />}
        />
      ) : (
        <CreateButton to={createUrl} />
      )}
      {/* Default is 1000; tenants have well over that, and a silently
          truncated CSV is worse than a slow one. */}
      <ExportButton maxResults={25000} />
      <RaButton
        component={Link}
        to="/job-application-calendar"
        label={translate('resources.job-application.actions.calendar')}
        startIcon={<CalendarMonthIcon />}
      />
      <RaButton
        component={Link}
        to="/job-application-dashboard"
        label={translate('resources.job-application.actions.dashboard')}
        startIcon={<DashboardIcon />}
      />
    </TopToolbar>
  );
};

// Helper function for user record representation (same as in resources/index.ts)
const getUserRecordRepresentation = (record: { name?: string; email?: string }) => {
  const name = record?.name || "";
  const email = record?.email || "";
  if (!name) {
    return email;
  }
  if (!email) {
    return name;
  }
  return `${name} <${email}>`;
};

// Behavioral profile choices for filter
const behavioralProfileChoices = [
  { id: "dominant", name: "resources.job-application.behavioralProfile.dominant" },
  { id: "influential", name: "resources.job-application.behavioralProfile.influential" },
  { id: "steady", name: "resources.job-application.behavioralProfile.steady" },
  { id: "compliant", name: "resources.job-application.behavioralProfile.compliant" },
];

// Custom toolbar with audit button next to delete button
const JobApplicationToolbar = (props: ToolbarProps) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const recordId = useMemo(() => {
    if (!record?.id) {
      return null;
    }

    const idString = String(record.id);
    if (idString === 'create' || idString === '') {
      return null;
    }

    const id = Number(record.id);
    if (isNaN(id) || id <= 0 || !Number.isInteger(id)) {
      return null;
    }

    return id;
  }, [record?.id]);

  const [isAuditDialogOpen, setIsAuditDialogOpen] = useState(false);

  return (
    <>
      <Toolbar {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <SaveButton />
        <Box display="flex" gap={1}>
          {recordId && (
            <RaButton
              label={translate('resources.job-application.audit.button')}
              onClick={() => setIsAuditDialogOpen(true)}
              startIcon={<HistoryIcon />}
            />
          )}
          <DeleteButton mutationMode="pessimistic" />
        </Box>
      </Toolbar>

      {recordId && (
        <Dialog
          open={isAuditDialogOpen}
          onClose={() => setIsAuditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{translate('resources.job-application.audit.title')}</DialogTitle>
          <DialogContent dividers>
            <AuditTimelineCompact
              resourceType="job-application"
              resourceId={recordId}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAuditDialogOpen(false)}>
              {translate('resources.job-application.audit.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

// Component to wrap form with audit button in toolbar
const JobApplicationFormWithAudit = (props: Record<string, unknown>) => {
  return <JobApplicationForm {...props} toolbar={<JobApplicationToolbar />} />;
};

// StackedFilters configuration with advanced operators
// Using manual configuration for fields that need null/not_null operators
const jobApplicationFiltersConfig: FiltersConfig = {
  candidateName: {
    operators: [
      { value: 'q', label: 'ra-form-layout.filters.operators.q', type: 'single' },
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'ne', label: 'ra-form-layout.filters.operators.ne', type: 'single' },
    ],
    input: ({ source }) => <TextInput source={source} label="resources.job-application.fields.candidateName" />,
  },
  email: {
    operators: [
      { value: 'q', label: 'ra-form-layout.filters.operators.q', type: 'single' },
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'ne', label: 'ra-form-layout.filters.operators.ne', type: 'single' },
    ],
    input: ({ source }) => <TextInput source={source} label="resources.job-application.fields.email" />,
  },
  phone: {
    operators: [
      { value: 'q', label: 'ra-form-layout.filters.operators.q', type: 'single' },
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'ne', label: 'ra-form-layout.filters.operators.ne', type: 'single' },
    ],
    input: ({ source }) => <TextInput source={source} label="resources.job-application.fields.phone" />,
  },
  city: {
    operators: [
      { value: 'q', label: 'ra-form-layout.filters.operators.q', type: 'single' },
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'ne', label: 'ra-form-layout.filters.operators.ne', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => <TextInput source={source} label="resources.job-application.fields.city" />,
  },
  birthDate: {
    operators: [
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'gte', label: 'ra-form-layout.filters.operators.gte', type: 'single' },
      { value: 'lte', label: 'ra-form-layout.filters.operators.lte', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => <DateInput source={source} label="resources.job-application.fields.birthDate" />,
  },
  createdAt: {
    operators: [
      { value: 'gte', label: 'ra-form-layout.filters.operators.gte', type: 'single' },
      { value: 'lte', label: 'ra-form-layout.filters.operators.lte', type: 'single' },
    ],
    input: ({ source }) => <DateInput source={source} label="resources.job-application.filters.createdFrom" />,
  },
  interviewDate: {
    operators: [
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'gte', label: 'ra-form-layout.filters.operators.gte', type: 'single' },
      { value: 'lte', label: 'ra-form-layout.filters.operators.lte', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => <DateInput source={source} label="resources.job-application.fields.interviewDate" />,
  },
  startedAt: {
    operators: [
      { value: 'eq', label: 'ra-form-layout.filters.operators.eq', type: 'single' },
      { value: 'gte', label: 'ra-form-layout.filters.operators.gte', type: 'single' },
      { value: 'lte', label: 'ra-form-layout.filters.operators.lte', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => <DateInput source={source} label="resources.job-application.fields.startedAt" />,
  },
  statusId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="job-application-status">
        <AutocompleteArrayInput 
          optionText="name" 
          label="resources.job-application.fields.statusId"
          filterToQuery={(searchText) => ({ name_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  companyId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="company">
        <AutocompleteArrayInput 
          optionText="name" 
          label="resources.job-application.fields.companyId"
          filterToQuery={(searchText) => ({ name_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  sourceId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="job-application-source">
        <AutocompleteArrayInput 
          optionText="name" 
          label="resources.job-application.fields.sourceId"
          filterToQuery={(searchText) => ({ name_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  interviewResponsibleId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="user">
        <AutocompleteArrayInput 
          optionText={getUserRecordRepresentation}
          label="resources.job-application.fields.interviewResponsibleId"
          filterToQuery={(searchText) => ({ nameEmail_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  interviewSchedulingResponsibleId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="user">
        <AutocompleteArrayInput 
          optionText={getUserRecordRepresentation}
          label="resources.job-application.fields.interviewSchedulingResponsibleId"
          filterToQuery={(searchText) => ({ nameEmail_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  supervisorId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="user">
        <AutocompleteArrayInput 
          optionText={getUserRecordRepresentation}
          label="resources.job-application.fields.supervisorId"
          filterToQuery={(searchText) => ({ nameEmail_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
  behavioralProfile: {
    operators: [
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <AutocompleteArrayInput
        source={source}
        choices={behavioralProfileChoices}
        label="resources.job-application.fields.behavioralProfile"
      />
    ),
  },
  outcome: {
    operators: [
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <SelectInput
        source={source}
        choices={[
          { id: 'hired', name: 'resources.job-application.fields.outcomeChoices.hired' },
          { id: 'closed', name: 'resources.job-application.fields.outcomeChoices.closed' },
          { id: 'terminated', name: 'resources.job-application.fields.outcomeChoices.terminated' },
        ]}
        label="resources.job-application.fields.outcome"
      />
    ),
  },
  outcomeAt: {
    operators: [
      { value: 'gte', label: 'ra-form-layout.filters.operators.gte', type: 'single' },
      { value: 'lte', label: 'ra-form-layout.filters.operators.lte', type: 'single' },
    ],
    input: ({ source }) => <DateInput source={source} label="resources.job-application.fields.outcomeAt" />,
  },
  closureReasonId: {
    operators: [
      { value: 'in', label: 'ra-form-layout.filters.operators.in', type: 'multiple' },
      { value: 'eq', label: 'resources.job-application.filters.operators.equals', type: 'single' },
      { value: 'null', label: 'ra-form-layout.filters.operators.null', type: 'single', input: () => <EmptyInput />, defaultValue: true },
      { value: 'notnull', label: 'ra-form-layout.filters.operators.notnull', type: 'single', input: () => <EmptyInput />, defaultValue: true },
    ],
    input: ({ source }) => (
      <ReferenceArrayInput source={source} reference="job-application-closure-reason">
        <AutocompleteArrayInput
          optionText="name"
          label="resources.job-application.fields.closureReasonName"
          filterToQuery={(searchText) => ({ name_q: searchText })}
        />
      </ReferenceArrayInput>
    ),
  },
} as FiltersConfig;

// Helper function to get outcome label with translation
const getOutcomeLabel = (translate: (key: string) => string, outcome: string): { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } => {
  const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    hired: 'success',
    closed: 'warning',
    terminated: 'error',
  };
  
  const translationKey = `resources.job-application.fields.outcomeChoices.${outcome}`;
  const translated = translate(translationKey);
  const label = translated !== translationKey ? translated : outcome;
  
  return {
    label,
    color: colorMap[outcome] || 'default',
  };
};

// View types
type ViewType = 'pipeline' | 'list';

// Pipeline View Component (Kanban-style visualization)
const KanbanView = ({ refreshToken }: { refreshToken?: number }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { filterValues } = useListContext();
  
  const [pipelineApplications, setPipelineApplications] = useState<JobApplication[]>([]);
  const [isPipelineLoading, setIsPipelineLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Fetch pipeline applications from dedicated endpoint with filters
  // Uses the same filter system as list view (POST with body, same format as /search)
  const fetchPipelineData = async (options?: { showLoading?: boolean }) => {
    try {
      const showLoading = options?.showLoading ?? true;
      if (showLoading) {
        setIsPipelineLoading(true);
      }
      
      // Map filters using the same function that dataProvider uses
      const mapReactAdminFiltersToBackend = (filters: Record<string, unknown>): Record<string, unknown> => {
        const backendFilters: Record<string, unknown> = {};
        const validOperators = ['q', 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'null', 'notnull'];
        
        const hasOperator = (key: string): boolean => {
          const parts = key.split('_');
          if (parts.length < 2) return false;
          const operator = parts[parts.length - 1];
          return validOperators.includes(operator);
        };

        const formatDateWithTime = (dateValue: string, operator: string): string => {
          const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateOnlyRegex.test(dateValue)) {
            if (operator === 'gte') {
              const date = new Date(`${dateValue}T00:00:00`);
              return date.toISOString();
            } else if (operator === 'lte') {
              const date = new Date(`${dateValue}T23:59:59.999`);
              return date.toISOString();
            }
            return new Date(dateValue).toISOString();
          }
          
          const datetimeLocalRegex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})$/;
          const datetimeLocalMatch = dateValue.match(datetimeLocalRegex);
          if (datetimeLocalMatch) {
            const baseDateTime = datetimeLocalMatch[1];
            if (operator === 'gte') {
              return new Date(`${baseDateTime}:00`).toISOString();
            } else if (operator === 'lte') {
              return new Date(`${baseDateTime}:59.999`).toISOString();
            }
            return new Date(`${baseDateTime}:00`).toISOString();
          }
          
          const parsedDate = new Date(dateValue);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString();
          }
          
          return dateValue;
        };

        for (const [key, value] of Object.entries(filters)) {
          const parts = key.split('_');
          const operator = parts.length >= 2 ? parts[parts.length - 1] : '';
          if (operator === 'null' || operator === 'notnull') {
            backendFilters[key] = (value !== undefined && value !== null && value !== '') ? value : true;
            continue;
          }

          if (value === undefined || value === null || value === '') continue;
          if (Array.isArray(value) && value.length === 0) continue;

          if (hasOperator(key)) {
            if (typeof value === 'string' && (operator === 'gte' || operator === 'lte')) {
              backendFilters[key] = formatDateWithTime(value, operator);
            } else {
              backendFilters[key] = value;
            }
            continue;
          }

          if (Array.isArray(value)) {
            backendFilters[`${key}_in`] = value;
          } else {
            backendFilters[`${key}_q`] = value;
          }
        }

        return backendFilters;
      };
      
      // Map filters to backend format (same as dataProvider.getList)
      const backendFilters = filterValues ? mapReactAdminFiltersToBackend(filterValues) : {};
      
      // Build request body (same format as /search endpoint)
      const requestBody = {
        ...backendFilters,
      };
      
      const { json } = await httpClient(`${API_URL}/job-application/kanban`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }) as { json: { data: { data: JobApplication[]; hasMore: boolean; total: number } } };
      
      // Backend returns: { data: { data: [...], hasMore: boolean, total: number } }
      const response = json.data;
      setPipelineApplications(response.data || []);
      setHasMore(response.hasMore || false);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching pipeline applications:', error);
      setPipelineApplications([]);
      setHasMore(false);
      setTotal(0);
    } finally {
      setIsPipelineLoading(false);
    }
  };

  // Fetch transition validation config
  useEffect(() => {
    const fetchTransitionValidationConfig = async () => {
      try {
        const { httpClient, API_URL } = await import('../../httpClient');
        const response = await httpClient(`${API_URL}/job-application-status/config/transition-validation`, {
          method: 'GET',
        });
        const result = response as { json: { data: { enforceStatusTransitionValidation: boolean } } };
        setEnforceStatusTransitionValidation(result.json.data.enforceStatusTransitionValidation);
      } catch (error) {
        console.error('Error loading transition validation config:', error);
        // Default to true if can't load (safer default)
        setEnforceStatusTransitionValidation(true);
      }
    };
    fetchTransitionValidationConfig();
  }, []);

  useEffect(() => {
    fetchPipelineData({ showLoading: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  // Force-refresh kanban data (used after create)
  useEffect(() => {
    if (!refreshToken) return;
    fetchPipelineData({ showLoading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // Fetch statuses
  const { data: statuses = [] } = useGetList('job-application-status', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'order', order: 'ASC' },
  });
  
  const [applicationsByStatus, setApplicationsByStatus] = useState<Record<string, JobApplication[]>>({});
  // Pending move to a terminal status that needs a closure reason before commit.
  const [closurePending, setClosurePending] = useState<{
    movedApp: JobApplication;
    targetStatusId: number;
    currentStatusId: number | null | undefined;
    fromStatusIdStr: string;
    toStatusIdStr: string;
    previousState: Record<string, JobApplication[]>;
  } | null>(null);
  const [allowedStatusesCache, setAllowedStatusesCache] = useState<Record<number, number[] | null>>({});
  const [enforceStatusTransitionValidation, setEnforceStatusTransitionValidation] = useState<boolean>(true);
  const kanbanScrollRef = useRef<HTMLDivElement | null>(null);
  const kanbanContentRef = useRef<HTMLDivElement | null>(null);
  const kanbanTrackRef = useRef<HTMLDivElement | null>(null);
  const [kanbanThumb, setKanbanThumb] = useState<{ left: number; width: number; visible: boolean }>({
    left: 0,
    width: 0,
    visible: false,
  });
  const dragStateRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);

  const updateKanbanThumb = useCallback(() => {
    const scroller = kanbanScrollRef.current;
    const track = kanbanTrackRef.current;
    if (!scroller || !track) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    const trackWidth = track.clientWidth;

    if (!trackWidth || scrollWidth <= clientWidth + 1) {
      setKanbanThumb((prev) => {
        const next = { left: 0, width: 0, visible: false };
        return (prev.left === next.left && prev.width === next.width && prev.visible === next.visible) ? prev : next;
      });
      return;
    }

    const minThumb = 32;
    const width = Math.max(minThumb, Math.round((clientWidth / scrollWidth) * trackWidth));
    const maxLeft = Math.max(0, trackWidth - width);
    const maxScroll = Math.max(1, scrollWidth - clientWidth);
    const left = Math.round((scrollLeft / maxScroll) * maxLeft);

    setKanbanThumb((prev) => {
      const next = { left, width, visible: true };
      return (prev.left === next.left && prev.width === next.width && prev.visible === next.visible) ? prev : next;
    });
  }, []);

  useEffect(() => {
    updateKanbanThumb();
  }, [updateKanbanThumb, applicationsByStatus, statuses]);

  useEffect(() => {
    const scroller = kanbanScrollRef.current;
    if (!scroller) return;

    const onScroll = () => updateKanbanThumb();
    scroller.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => updateKanbanThumb());
    ro.observe(scroller);
    if (kanbanContentRef.current) {
      ro.observe(kanbanContentRef.current);
    }

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [updateKanbanThumb]);

  // Wheel-to-horizontal scroll (must be non-passive to allow preventDefault)
  useEffect(() => {
    const scroller = kanbanScrollRef.current;
    if (!scroller) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle wheel-to-horizontal when there is horizontal overflow.
      if (scroller.scrollWidth <= scroller.clientWidth + 1) {
        return;
      }

      // If the wheel event originated inside a vertically-scrollable element (e.g., a kanban column
      // or Virtuoso scroller), let it scroll vertically and don't hijack it for horizontal scrolling.
      const target = e.target as HTMLElement | null;
      const hasScrollableVerticalParent = (el: HTMLElement | null): boolean => {
        let current: HTMLElement | null = el;
        while (current && current !== scroller) {
          const style = window.getComputedStyle(current);
          const overflowY = style.overflowY;
          const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight + 1;
          if (canScrollY) {
            return true;
          }
          current = current.parentElement;
        }
        return false;
      };

      // If there's a vertical scrollable parent and the user is scrolling vertically, don't intercept.
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && hasScrollableVerticalParent(target)) {
        return;
      }

      // Many devices send deltaY for wheel; translate into horizontal scroll for the kanban.
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

      if (delta !== 0) {
        scroller.scrollLeft += delta;
        e.preventDefault();
      }
    };

    scroller.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scroller.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Backend returns pipeline applications (sem resultado ou com resultado recente)
  // Backend now handles filtering for statusId, companyId, sourceId
  // Other filters are not supported by kanban endpoint (use list view for advanced filtering)
  const activeApplications = useMemo(() => {
    return pipelineApplications || [];
  }, [pipelineApplications]);

  // Show all statuses in pipeline view (including post-hire and hidesFromKanban)
  // Derive directly from react-admin query results to avoid infinite loops caused by unstable array references.
  const visibleStatuses = useMemo(() => {
    return statuses as Array<StatusData>;
  }, [statuses]);

  // Memoize status stages to avoid recalculating on every render
  const statusStages = useMemo(() => {
    return getStatusStages(visibleStatuses);
  }, [visibleStatuses]);

  useEffect(() => {
    const newApplicationsByStatus = getApplicationsByStatus(activeApplications, statusStages);
    
    setApplicationsByStatus(prevState => {
      // Use shallow comparison instead of JSON.stringify for better performance
      const isDifferent = Object.keys(prevState).length !== Object.keys(newApplicationsByStatus).length ||
        Object.keys(newApplicationsByStatus).some(key => 
          prevState[key]?.length !== newApplicationsByStatus[key]?.length ||
          prevState[key]?.some((app, idx) => app.id !== newApplicationsByStatus[key]?.[idx]?.id)
        );
      return isDifferent ? newApplicationsByStatus : prevState;
    });
  }, [activeApplications, statusStages]);

  // Fetch allowed next statuses for a given status
  // Returns null on error (to distinguish from "no restrictions" which is empty array)
  const getAllowedNextStatuses = async (fromStatusId: number): Promise<number[] | null> => {
    if (allowedStatusesCache[fromStatusId] !== undefined) {
      return allowedStatusesCache[fromStatusId];
    }

    try {
      const { httpClient, API_URL } = await import('../../httpClient');
      const response = await httpClient(
        `${API_URL}/job-application-status-transition/allowed-next-statuses/${fromStatusId}`,
        { method: 'GET' }
      );
      const result = response as { json: { data: Array<{ id: number; name: string; order: number }> } };
      const allowedIds = result.json.data.map(s => s.id);
      setAllowedStatusesCache(prev => ({ ...prev, [fromStatusId]: allowedIds }));
      return allowedIds;
    } catch (error) {
      console.error('Error fetching allowed next statuses:', error);
      // Return null on error to distinguish from "no restrictions" (empty array)
      // This allows the caller to handle errors appropriately
      return null;
    }
  };

  if (isPipelineLoading) return null;

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // Dropped outside a droppable
    if (!destination) return;
    
    // Dropped in same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const fromStatusIdStr = source.droppableId;
    const toStatusIdStr = destination.droppableId;

    const sourceItems = applicationsByStatus[fromStatusIdStr] || [];
    const movedApp = sourceItems.find((a) => String(a.id) === draggableId);
    if (!movedApp) return;

    const currentStatusId = movedApp.statusId;
    const targetStatusId = Number(toStatusIdStr);

    // Reorder within same column (no backend call needed for reordering)
    if (source.droppableId === destination.droppableId) {
      const items = [...sourceItems];
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);
      setApplicationsByStatus((prev) => ({
        ...prev,
        [fromStatusIdStr]: items,
      }));
      return;
    }

    // Check if transition is allowed before attempting (only if validation is enabled)
    if (enforceStatusTransitionValidation && currentStatusId) {
      const allowedStatuses = await getAllowedNextStatuses(currentStatusId);
      
      // If error fetching (null), don't block but let backend validate
      // If empty array, it means no explicit restrictions (all transitions allowed)
      // If array with items, check if target is in the list
      if (allowedStatuses !== null && allowedStatuses.length > 0 && !allowedStatuses.includes(targetStatusId)) {
        const currentStatus = statuses.find(s => s.id === currentStatusId);
        const targetStatus = statuses.find(s => s.id === targetStatusId);
        const allowedStatusNames = statuses
          .filter(s => allowedStatuses.includes(s.id))
          .map(s => s.name)
          .join(', ');

        notify(
          translate('resources.job-application.statusTransitionNotAllowed', {
            currentStatus: currentStatus?.name || currentStatusId,
            targetStatus: targetStatus?.name || targetStatusId,
            allowedStatuses: allowedStatusNames,
          }),
          { type: 'error' }
        );
        return;
      }
      
      // If error fetching (null), show warning but proceed (backend will validate)
      if (allowedStatuses === null) {
        notify(
          translate('resources.job-application.statusTransitionValidationWarning', {
            defaultValue: 'Unable to validate transition. Backend will verify.',
          }),
          { type: 'warning' }
        );
      }
    }

    // If target is a terminal status that requires a closure reason, defer the
    // commit: open the dialog, capture the reason, then commit with it.
    const targetStatus = statuses.find((s) => s.id === targetStatusId);
    if (targetStatus?.isTerminal && targetStatus?.requiresClosureReason) {
      // Optimistic UI move (will be reverted on cancel).
      const previousState = { ...applicationsByStatus };
      const destItems = applicationsByStatus[toStatusIdStr] || [];
      const nextSource = [...sourceItems];
      nextSource.splice(source.index, 1);
      const nextDest = [...destItems];
      nextDest.splice(destination.index, 0, {
        ...movedApp,
        statusId: targetStatusId,
      });
      setApplicationsByStatus((prev) => ({
        ...prev,
        [fromStatusIdStr]: nextSource,
        [toStatusIdStr]: nextDest,
      }));

      setClosurePending({
        movedApp,
        targetStatusId,
        currentStatusId,
        fromStatusIdStr,
        toStatusIdStr,
        previousState,
      });
      return;
    }

    // Optimistically update UI (after validation check)
    const previousState = { ...applicationsByStatus };
    const destItems = applicationsByStatus[toStatusIdStr] || [];

    const nextSource = [...sourceItems];
    nextSource.splice(source.index, 1);
    const nextDest = [...destItems];
    nextDest.splice(destination.index, 0, { ...movedApp, statusId: targetStatusId });

    setApplicationsByStatus((prev) => ({
      ...prev,
      [fromStatusIdStr]: nextSource,
      [toStatusIdStr]: nextDest,
    }));

    await commitStatusMove({
      movedApp,
      targetStatusId,
      currentStatusId,
      previousState,
    });
  };

  /**
   * Commits a status move via the data provider. When closureReasonId is
   * provided (terminal status), it is included so the backend can finalise
   * the outcome. On failure the optimistic UI is rolled back.
   */
  const commitStatusMove = async ({
    movedApp,
    targetStatusId,
    currentStatusId,
    previousState,
    closureReasonId,
  }: {
    movedApp: JobApplication;
    targetStatusId: number;
    currentStatusId: number | null | undefined;
    previousState: Record<string, JobApplication[]>;
    closureReasonId?: number;
  }) => {
    try {
      await dataProvider.update("job-application", {
        id: movedApp.id,
        data: {
          ...movedApp,
          statusId: targetStatusId,
          ...(closureReasonId !== undefined ? { closureReasonId } : {}),
        },
        previousData: movedApp,
      });
      notify('resources.job-application.statusUpdated', { type: 'success' });
      // Clear cache for both old and new status to refresh allowed transitions
      setAllowedStatusesCache(prev => {
        const newCache = { ...prev };
        if (currentStatusId) delete newCache[currentStatusId];
        delete newCache[targetStatusId];
        return newCache;
      });
      fetchPipelineData({ showLoading: false });
    } catch (error: unknown) {
      setApplicationsByStatus(previousState);

      type ErrorResponse = {
        errorCode?: string;
        message?: string;
        currentStatusName?: string;
        attemptedStatusName?: string;
        allowedStatusNames?: string[];
      };

      const errorObj = error as { body?: ErrorResponse; response?: ErrorResponse; message?: string };
      const errorResponse: ErrorResponse = errorObj?.body || errorObj?.response || {};
      const errorCode = errorResponse.errorCode;
      const errorMessage = errorResponse.message || errorObj?.message || translate('resources.job-application.statusUpdateError');

      if (errorCode === 'STATUS_TRANSITION_NOT_ALLOWED') {
        notify(
          translate('resources.job-application.statusTransitionNotAllowed', {
            currentStatus: errorResponse.currentStatusName || currentStatusId,
            targetStatus: errorResponse.attemptedStatusName || targetStatusId,
            allowedStatuses: errorResponse.allowedStatusNames?.join(', ') || '',
          }),
          { type: 'error' }
        );
      } else if (errorCode === 'POST_HIRE_TO_PRE_HIRE_TRANSITION') {
        notify(
          translate('resources.job-application.postHireToPreHireTransition', {
            currentStatus: errorResponse.currentStatusName || currentStatusId,
            targetStatus: errorResponse.attemptedStatusName || targetStatusId,
          }),
          { type: 'error' }
        );
      } else if (errorCode === 'OUTCOME_TO_PIPELINE_TRANSITION') {
        notify(
          translate('resources.job-application.outcomeToPipelineTransition', {
            currentStatus: errorResponse.currentStatusName || currentStatusId,
            targetStatus: errorResponse.attemptedStatusName || targetStatusId,
          }),
          { type: 'error' }
        );
      } else if (errorCode === 'SKIP_HIRE_STATUS') {
        notify(
          translate('resources.job-application.skipHireStatus', {
            targetStatus: errorResponse.attemptedStatusName || targetStatusId,
          }),
          { type: 'error' }
        );
      } else {
        notify(errorMessage, { type: 'error' });
      }
    }
  };

  const handleClosureConfirm = async (closureReasonId: number) => {
    if (!closurePending) return;
    const { movedApp, targetStatusId, currentStatusId, previousState } =
      closurePending;
    setClosurePending(null);
    await commitStatusMove({
      movedApp,
      targetStatusId,
      currentStatusId,
      previousState,
      closureReasonId,
    });
  };

  const handleClosureCancel = () => {
    if (!closurePending) return;
    // Roll back the optimistic move.
    setApplicationsByStatus(closurePending.previousState);
    setClosurePending(null);
  };

  return (
    <>
      {hasMore && (
        <Alert severity="info" sx={{ mb: 2, mx: 2 }}>
          {translate('resources.job-application.kanban.hasMore', {
            count: pipelineApplications.length,
            total: total,
          })}
        </Alert>
      )}
      {closurePending && (
        <CloseApplicationDialog
          open={!!closurePending}
          onClose={handleClosureCancel}
          onConfirm={handleClosureConfirm}
          application={closurePending.movedApp}
        />
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        {/* Local horizontal scrolling for the kanban board (prevents full-page horizontal scroll) */}
        <Box sx={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={kanbanScrollRef}
            data-testid="kanban-scroller"
            tabIndex={0}
            sx={{
              width: '100%',
              minWidth: 0,
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              // Reserve space so the horizontal scrollbar doesn't visually overlap the columns' vertical scroll.
              // (Some platforms render the horizontal scrollbar on top of content.)
              pb: 3,
              // When supported, reserve gutter for the scrollbar instead of overlaying content.
              scrollbarGutter: 'stable',
              outline: 'none',
              overscrollBehaviorX: 'contain',
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px', '&:hover': { backgroundColor: '#a8a8a8' } },
            }}
          >
            <Box
              ref={kanbanContentRef}
              sx={{
                // Ensure we overflow horizontally when there are many columns.
                minWidth: `calc(${Math.max(1, visibleStatuses.length)} * 320px)`,
                width: 'max-content',
              }}
            >
              <Box display="flex" gap={2} sx={{ paddingX: 2, paddingTop: 4, paddingBottom: 1 }}>
                {getStatusStages(visibleStatuses).map((stage) => (
                  <JobApplicationColumn
                    key={stage.id}
                    statusId={stage.id}
                    applications={applicationsByStatus[stage.id] || []}
                    label={stage.label}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Always-visible custom scrollbar (works even when OS uses overlay/hidden scrollbars) */}
          {kanbanThumb.visible && (
            <Box
              ref={kanbanTrackRef}
              sx={{
                height: 10,
                mx: 2,
                mb: 1,
                borderRadius: 999,
                backgroundColor: 'rgba(0,0,0,0.08)',
                position: 'relative',
                userSelect: 'none',
              }}
            >
              <Box
                onPointerDown={(e) => {
                  const scroller = kanbanScrollRef.current;
                  const track = kanbanTrackRef.current;
                  if (!scroller || !track) return;
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  dragStateRef.current = { startX: e.clientX, startScrollLeft: scroller.scrollLeft };
                }}
                onPointerMove={(e) => {
                  const scroller = kanbanScrollRef.current;
                  const track = kanbanTrackRef.current;
                  const drag = dragStateRef.current;
                  if (!scroller || !track || !drag) return;

                  const { scrollWidth, clientWidth } = scroller;
                  const trackWidth = track.clientWidth;
                  const thumbWidth = kanbanThumb.width;
                  const maxScroll = Math.max(1, scrollWidth - clientWidth);
                  const maxThumbMove = Math.max(1, trackWidth - thumbWidth);

                  const deltaX = e.clientX - drag.startX;
                  const scrollDelta = (deltaX / maxThumbMove) * maxScroll;
                  scroller.scrollLeft = drag.startScrollLeft + scrollDelta;
                }}
                onPointerUp={() => {
                  dragStateRef.current = null;
                }}
                sx={{
                  position: 'absolute',
                  left: kanbanThumb.left,
                  top: 0,
                  height: '100%',
                  width: kanbanThumb.width,
                  borderRadius: 999,
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  cursor: 'grab',
                }}
              />
            </Box>
          )}
        </Box>
      </DragDropContext>
    </>
  );
};


// List View Content Component
const ListViewContent = ({ onEditClick }: { onEditClick?: (id: string | number) => void }) => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  
  // Fetch statuses to display status name without link
  const { data: statuses = [] } = useGetList('job-application-status', {
    pagination: { page: 1, perPage: 100 },
  });
  
  const getStatusName = (statusId: number | undefined | null): string => {
    if (!statusId) return '';
    const status = statuses.find((s: { id: number }) => s.id === statusId);
    return status?.name || '';
  };
  
  // Custom rowClick handler - use onEditClick if provided (standalone mode), otherwise use routing
  const handleRowClick = (id: string | number) => {
    if (onEditClick) {
      onEditClick(id);
      return false; // Prevent default navigation
    }
    const viewParam = searchParams.get('view');
    if (viewParam === 'list') {
      return `/job-application/${id}?view=list`;
    }
    return `/job-application/${id}`;
  };
  
  return (
    <Datagrid rowClick={handleRowClick} bulkActionButtons={false}>
      <TextField source="candidateName" label={translate('resources.job-application.fields.candidateName')} />
      <TextField source="email" label={translate('resources.job-application.fields.email')} />
      <PhoneField source="phone" label={translate('resources.job-application.fields.phone')} defaultCountry="BR" />
      <FunctionField
        label={translate('resources.job-application.fields.statusId')}
        render={(record: JobApplication) => getStatusName(record.statusId)}
      />
      <FunctionField
        label={translate('resources.job-application.fields.closureOutcome')}
        render={(record: JobApplication) => {
          if (!record.outcome) return null;
          const outcomeInfo = getOutcomeLabel(translate, record.outcome);
          return <Chip label={outcomeInfo.label} color={outcomeInfo.color} size="small" />;
        }}
      />
      {/* A hire has an outcomeAt too; showing it here would read as a departure date for active employees. */}
      <FunctionField
        source="outcomeAt"
        label={translate('resources.job-application.fields.outcomeAt')}
        render={(record: JobApplication) => {
          if (!record.outcomeAt || record.outcome === 'hired') return null;
          return <DateField source="outcomeAt" locales={DATE_FIELD_LOCALES} />;
        }}
      />
      {/* Denormalized on the search payload, so not sortable server-side. */}
      <TextField
        source="closureReasonName"
        sortable={false}
        label={translate('resources.job-application.fields.closureReasonName')}
      />
      <FunctionField
        source="closureReasonCategory"
        sortable={false}
        label={translate('resources.job-application.fields.closureReasonCategory')}
        render={(record: JobApplication) =>
          record.closureReasonCategory
            ? // Canonical labels for this enum — same ones the closure-reason
              // admin screen and the Conversion dashboard render.
              translate(
                `resources.job-application-closure-reason.categoryChoices.${record.closureReasonCategory}`,
                { _: record.closureReasonCategory },
              )
            : null
        }
      />
      <ReferenceField source="sourceId" reference="job-application-source" label={translate('resources.job-application.fields.sourceId')}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="companyId" reference="company" label={translate('resources.job-application.fields.companyId')}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="supervisorId" reference="user" label={translate('resources.job-application.fields.supervisorId')} link={false}>
        <FunctionField render={(record: { name?: string; email?: string }) => getUserRecordRepresentation(record)} />
      </ReferenceField>
      <TextField source="city" label={translate('resources.job-application.fields.city')} />
      <DateField source="createdAt" locales={DATE_FIELD_LOCALES} label={translate('resources.job-application.fields.createdAt')} />
      <DateField source="interviewDate" locales={DATE_FIELD_LOCALES} label={translate('resources.job-application.fields.interviewDate')} showTime />
      <EditButton />
    </Datagrid>
  );
};

// Shared header component for all views
const ViewHeader = ({ currentView, onViewChange }: { currentView: ViewType; onViewChange: (event: unknown, value: ViewType) => void }) => {
  const translate = useTranslate();
  
  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
        <Tabs value={currentView} onChange={onViewChange} sx={{ minHeight: 48 }}>
          <Tab 
            icon={<ViewKanbanIcon />} 
            iconPosition="start" 
            value="pipeline" 
            sx={{ minHeight: 48 }} 
            label={
              <Tooltip 
                title={translate('resources.job-application.views.pipelineTooltip')}
                arrow
              >
                <span>{translate('resources.job-application.views.kanban')}</span>
              </Tooltip>
            }
          />
          <Tab 
            icon={<ListIcon />} 
            iconPosition="start" 
            value="list" 
            sx={{ minHeight: 48 }} 
            label={
              <Tooltip 
                title={translate('resources.job-application.views.listTooltip')}
                arrow
              >
                <span>{translate('resources.job-application.views.list')}</span>
              </Tooltip>
            }
          />
        </Tabs>
      </Box>
    </Box>
  );
};

export const JobApplicationList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView = (searchParams.get('view') as ViewType) || 'pipeline';
  const notify = useNotify();
  const redirect = useRedirect();
  const [kanbanRefreshToken, setKanbanRefreshToken] = useState(0);

  // All hooks must be called before any conditional returns
  // For List view, use standalone mode for modals to maintain view=list parameter
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);

  const openCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
    // Preserve existing search params (including filters) and ensure we stay in list view
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('view', 'list');
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const openEditDialog = useCallback((id: string | number) => {
    setEditingId(id);
    setIsEditDialogOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditingId(null);
    // Preserve existing search params (including filters) and ensure we stay in list view
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('view', 'list');
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handleViewChange = (_: unknown, newView: ViewType) => {
    setSearchParams({ view: newView });
  };

  // For Pipeline view, use List wrapper to enable filters
  if (currentView === 'pipeline') {
    return (
      <>
        <List
          perPage={100}
          pagination={false}
          sort={{ field: 'id', order: 'DESC' }}
          actions={<CustomListActions />}
          filters={
            <StackedFilters
              config={jobApplicationFiltersConfig}
              PopoverProps={{
                PaperProps: {
                  sx: {
                    minWidth: 600,
                    maxWidth: 800,
                  },
                },
              }}
            />
          }
          empty={false}
        >
          <Box sx={{ position: 'relative', width: '100%', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <ViewHeader currentView={currentView} onViewChange={handleViewChange} />
            {/* Constrain horizontal size (to enable horizontal scroll) while keeping a stable min height */}
            <Box
              sx={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                minHeight: 'calc(100vh - 280px)',
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0 }}>
                <KanbanView refreshToken={kanbanRefreshToken} />
              </Box>
            </Box>
          </Box>
        </List>
        <CreateDialog
          key="pipeline-create"
          fullWidth
          maxWidth="md"
          mutationOptions={{
            onSuccess: () => {
              notify('ra.notification.created', {
                type: 'info',
                messageArgs: { smart_count: 1 },
              });
              setKanbanRefreshToken((t) => t + 1);
              redirect('list', 'job-application', undefined, undefined, {
                _scrollToTop: false,
              });
            },
          }}
        >
          <JobApplicationForm />
        </CreateDialog>
        <EditDialog key="pipeline-edit" fullWidth maxWidth="md" mutationMode="pessimistic">
          <JobApplicationFormWithAudit />
        </EditDialog>
      </>
    );
  }

  // For List view, use List component with advanced filters
  // Users can filter by outcome (null/notnull) to see pipeline vs finalized applications

  return (
    <>
      <List
        perPage={25}
        pagination={<Pagination />}
        sort={{ field: 'id', order: 'DESC' }}
        actions={<CustomListActions onCreateClick={openCreateDialog} />}
        filters={
          <StackedFilters
            config={jobApplicationFiltersConfig}
            PopoverProps={{
              PaperProps: {
                sx: {
                  minWidth: 600,
                  maxWidth: 800,
                },
              },
            }}
          />
        }
        empty={false}
      >
        <Box sx={{ position: 'relative', width: '100%', maxWidth: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <ViewHeader currentView={currentView} onViewChange={handleViewChange} />
          <ListViewContent onEditClick={openEditDialog} />
        </Box>
      </List>
      <CreateDialog 
        key="list-create" 
        fullWidth 
        maxWidth="md"
        isOpen={isCreateDialogOpen}
        open={openCreateDialog}
        close={closeCreateDialog}
        resource="job-application"
      >
        <JobApplicationForm />
      </CreateDialog>
      {editingId && (
        <EditDialog
          key={`list-edit-${editingId}`}
          fullWidth
          maxWidth="md"
          id={String(editingId)}
          isOpen={isEditDialogOpen}
          open={() => openEditDialog(editingId)}
          close={closeEditDialog}
          resource="job-application"
          mutationMode="pessimistic"
        >
          <JobApplicationFormWithAudit />
        </EditDialog>
      )}
    </>
  );
};
